/**
 * ImageCropDialog - Dialog para marcar área(s) de uma imagem
 *
 * Permite ao usuário:
 * 1. Visualizar a imagem do jornal/revista em tamanho grande
 * 2. Desenhar múltiplos retângulos para marcar áreas de interesse
 * 3. Escolher o tipo de marcação: OCR (extração de texto) ou Destaque (citação)
 * 4. Manter a página completa com as marcações visíveis
 *
 * TIPOS DE MARCADORES:
 * - OCR: Área para extração de texto via reconhecimento óptico
 * - Highlight: Destaque visual para citações ou referências
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Slider,
  Stack,
  Chip,
  Tooltip,
  AppBar,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  CropFree as CropIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Refresh as ResetIcon,
  Check as CheckIcon,
  Add as AddIcon,
  FormatColorFill as ColorIcon,
  TextFields as OcrIcon,
  Highlight as HighlightIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

// Cores para marcadores OCR (para identificar ordem de leitura)
const OCR_COLORS = [
  { bg: 'rgba(33, 150, 243, 0.25)', border: '#2196f3', name: 'Azul' },
  { bg: 'rgba(76, 175, 80, 0.25)', border: '#4caf50', name: 'Verde' },
  { bg: 'rgba(255, 152, 0, 0.25)', border: '#ff9800', name: 'Laranja' },
  { bg: 'rgba(156, 39, 176, 0.25)', border: '#9c27b0', name: 'Roxo' },
  { bg: 'rgba(244, 67, 54, 0.25)', border: '#f44336', name: 'Vermelho' },
  { bg: 'rgba(0, 188, 212, 0.25)', border: '#00bcd4', name: 'Ciano' },
];

// Cores para marcadores de destaque (highlighter)
const HIGHLIGHT_COLORS = [
  { bg: 'rgba(255, 235, 59, 0.45)', border: '#FBC02D', name: 'Amarelo' },
  { bg: 'rgba(129, 212, 250, 0.45)', border: '#03A9F4', name: 'Azul Claro' },
  { bg: 'rgba(165, 214, 167, 0.45)', border: '#4CAF50', name: 'Verde Claro' },
  { bg: 'rgba(255, 183, 77, 0.45)', border: '#FF9800', name: 'Laranja' },
  { bg: 'rgba(240, 98, 146, 0.45)', border: '#E91E63', name: 'Rosa' },
  { bg: 'rgba(186, 104, 200, 0.45)', border: '#9C27B0', name: 'Lilás' },
];

const getInitialCrop = (mode) => (
  mode === 'crop'
    ? {
      unit: '%',
      x: 20,
      y: 18,
      width: 60,
      height: 38
    }
    : {
      unit: '%',
      x: 10,
      y: 10,
      width: 80,
      height: 80
    }
);

const ImageCropDialog = ({
  open,
  onClose,
  imageSrc,
  onCropComplete,
  title = 'Marcar áreas da matéria',
  initialMarkers = [], // Marcadores existentes para edição
  mode = 'markers' // 'markers' para OCR/Destaques, 'crop' para thumbnail única
}) => {
  // Tipo de marcador atual: 'ocr' ou 'highlight'
  const [markerType, setMarkerType] = useState('ocr');

  // Cor selecionada para highlights
  const [highlightColorIndex, setHighlightColorIndex] = useState(0);

  // Crop atual sendo desenhado (em percentual)
  const [crop, setCrop] = useState({
    unit: '%',
    x: 10,
    y: 10,
    width: 80,
    height: 80
  });
  const [completedCrop, setCompletedCrop] = useState(null);

  // Array de marcadores salvos
  const [savedMarkers, setSavedMarkers] = useState([]);

  // Zoom como fator de escala da imagem
  const [zoomFactor, setZoomFactor] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  const imgRef = useRef(null);

  const getCanvasSafeImageSrc = useCallback((src) => {
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) {
      return src;
    }

    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}_canvas=${Date.now()}`;
  }, []);

  const canvasSafeImageSrc = useMemo(
    () => getCanvasSafeImageSrc(imageSrc),
    [getCanvasSafeImageSrc, imageSrc]
  );

  // Reset quando o dialog abre
  useEffect(() => {
    if (open) {
      // Carregar marcadores existentes ou iniciar vazio
      if (initialMarkers && initialMarkers.length > 0) {
        setSavedMarkers(initialMarkers.map((m, idx) => ({
          id: Date.now() + idx,
          type: m.type || 'ocr',
          percent: m.coordinates || m.percent,
          color: m.color || (m.type === 'highlight' ? HIGHLIGHT_COLORS[0].border : OCR_COLORS[idx % OCR_COLORS.length].border),
          label: m.label,
          order: m.order || idx
        })));
      } else {
        setSavedMarkers([]);
      }
      const initialCrop = getInitialCrop(mode);
      setCrop(initialCrop);
      setCompletedCrop(initialCrop); // Inicializar como válido para permitir confirmar sem mexer
      setZoomFactor(1);
      setImageLoaded(false);
      setNaturalSize({ width: 0, height: 0 });
      setMarkerType('ocr');
      setHighlightColorIndex(0);
    }
  }, [open, imageSrc, mode]);

  const onImageLoad = useCallback((e) => {
    const img = e.currentTarget;
    imgRef.current = img;
    setNaturalSize({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    setImageLoaded(true);

    const maxWidth = window.innerWidth * 0.9;
    const maxHeight = window.innerHeight * 0.7;

    const scaleW = maxWidth / img.naturalWidth;
    const scaleH = maxHeight / img.naturalHeight;
    const initialZoom = Math.min(scaleW, scaleH, 1);

    setZoomFactor(initialZoom);
  }, []);

  const handleResetCurrent = () => {
    const initialCrop = getInitialCrop(mode);
    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  };

  const handleClearAll = () => {
    setSavedMarkers([]);
    handleResetCurrent();
  };

  const isPercentageCrop = useCallback((cropData) => {
    if (!cropData) return false;
    if (cropData.unit === '%') {
      const maxX = cropData.x + cropData.width;
      const maxY = cropData.y + cropData.height;
      // Adicionar pequena tolerância para arredondamentos
      if (maxX <= 100.2 && maxY <= 100.2) {
        return true;
      }
    }
    return false;
  }, []);

  const getCropAsPixels = useCallback((cropData, img) => {
    if (!cropData || !img) return null;

    const imgWidth = img.width;
    const imgHeight = img.height;
    const naturalWidth = img.naturalWidth || imgWidth;
    const naturalHeight = img.naturalHeight || imgHeight;
    const isPct = isPercentageCrop(cropData);

    if (isPct) {
      return {
        x: (cropData.x / 100) * naturalWidth,
        y: (cropData.y / 100) * naturalHeight,
        width: (cropData.width / 100) * naturalWidth,
        height: (cropData.height / 100) * naturalHeight
      };
    }

    const scaleX = naturalWidth / imgWidth;
    const scaleY = naturalHeight / imgHeight;

    return {
      x: cropData.x * scaleX,
      y: cropData.y * scaleY,
      width: cropData.width * scaleX,
      height: cropData.height * scaleY
    };
  }, [isPercentageCrop]);

  /**
   * Adiciona um marcador à lista
   */
  const handleAddMarker = () => {
    if (!completedCrop || !imgRef.current) return;

    const imgWidth = imgRef.current.width;
    const imgHeight = imgRef.current.height;

    let percentX, percentY, percentW, percentH;
    const isPct = isPercentageCrop(completedCrop);

    if (isPct) {
      percentX = completedCrop.x;
      percentY = completedCrop.y;
      percentW = completedCrop.width;
      percentH = completedCrop.height;
    } else {
      percentX = (completedCrop.x / imgWidth) * 100;
      percentY = (completedCrop.y / imgHeight) * 100;
      percentW = (completedCrop.width / imgWidth) * 100;
      percentH = (completedCrop.height / imgHeight) * 100;
    }

    // Validar tamanho mínimo
    if (percentW < 2 || percentH < 2) {
      return;
    }

    // Determinar cor baseado no tipo
    const ocrCount = savedMarkers.filter(m => m.type === 'ocr').length;
    const color = markerType === 'ocr'
      ? OCR_COLORS[ocrCount % OCR_COLORS.length].border
      : HIGHLIGHT_COLORS[highlightColorIndex].border;

    const newMarker = {
      id: Date.now(),
      type: markerType,
      percent: {
        x: percentX,
        y: percentY,
        width: percentW,
        height: percentH
      },
      color: color,
      order: markerType === 'ocr' ? ocrCount : savedMarkers.length
    };

    setSavedMarkers(prev => [...prev, newMarker]);

    // Resetar crop para nova seleção
    setCrop({
      unit: '%',
      x: 5,
      y: 5,
      width: 30,
      height: 30
    });
    setCompletedCrop(null);
  };

  const handleRemoveMarker = (markerId) => {
    setSavedMarkers(prev => prev.filter(m => m.id !== markerId));
  };

  /**
   * Confirma as marcações e retorna os dados
   * No modo 'markers': retorna coordenadas dos marcadores e áreas OCR
   * No modo 'crop': retorna apenas a imagem recortada como string base64
   */
  const handleConfirm = async () => {
    // Modo Recorte Único (Thumbnail)
    if (mode === 'crop') {
      if (!completedCrop || !imgRef.current) return;

      try {
        const canvas = document.createElement('canvas');
        const img = imgRef.current;
        const pixelCrop = getCropAsPixels(completedCrop, img);

        if (!pixelCrop) return;

        canvas.width = Math.max(1, Math.round(pixelCrop.width));
        canvas.height = Math.max(1, Math.round(pixelCrop.height));

        const ctx = canvas.getContext('2d');
        ctx.drawImage(
          img,
          Math.round(pixelCrop.x),
          Math.round(pixelCrop.y),
          Math.round(pixelCrop.width),
          Math.round(pixelCrop.height),
          0,
          0,
          canvas.width,
          canvas.height
        );

        const base64Image = canvas.toDataURL('image/jpeg', 0.9);
        onCropComplete(base64Image);
        onClose();
        return;
      } catch (err) {
        console.error('Erro ao gerar recorte:', err);
        return;
      }
    }

    // Modo Marcadores Múltiplos (OCR)
    let allMarkers = [...savedMarkers];

    // Se há uma seleção atual válida, incluí-la
    if (completedCrop && imgRef.current) {
      const imgWidth = imgRef.current.width;
      const imgHeight = imgRef.current.height;

      let percentCrop;
      const isPct = isPercentageCrop(completedCrop);

      if (isPct) {
        percentCrop = {
          x: completedCrop.x,
          y: completedCrop.y,
          width: completedCrop.width,
          height: completedCrop.height
        };
      } else {
        percentCrop = {
          x: (completedCrop.x / imgWidth) * 100,
          y: (completedCrop.y / imgHeight) * 100,
          width: (completedCrop.width / imgWidth) * 100,
          height: (completedCrop.height / imgHeight) * 100
        };
      }

      if (percentCrop.width >= 2 && percentCrop.height >= 2) {
        const ocrCount = allMarkers.filter(m => m.type === 'ocr').length;
        const color = markerType === 'ocr'
          ? OCR_COLORS[ocrCount % OCR_COLORS.length].border
          : HIGHLIGHT_COLORS[highlightColorIndex].border;

        allMarkers.push({
          id: Date.now(),
          type: markerType,
          percent: percentCrop,
          color: color,
          order: markerType === 'ocr' ? ocrCount : allMarkers.length
        });
      }
    }

    if (allMarkers.length === 0) return;

    // Converter para formato do modelo
    const markersForModel = allMarkers.map(m => ({
      type: m.type,
      coordinates: {
        x: m.percent.x,
        y: m.percent.y,
        width: m.percent.width,
        height: m.percent.height
      },
      color: m.color,
      label: m.label,
      order: m.order
    }));

    // Separar marcadores OCR para extração de texto
    const ocrMarkers = markersForModel.filter(m => m.type === 'ocr');

    // Retornar: imagem original (não recortada), marcadores, e áreas OCR para extração
    onCropComplete({
      markers: markersForModel,
      ocrAreas: ocrMarkers,
      keepFullImage: true
    });

    onClose();
  };

  /**
   * Usa a imagem completa sem marcações
   */
  const handleUseFullImage = () => {
    onCropComplete({
      markers: [],
      ocrAreas: [],
      keepFullImage: true
    });
    onClose();
  };

  // Tamanho da imagem exibida
  const displayWidth = naturalSize.width * zoomFactor;
  const displayHeight = naturalSize.height * zoomFactor;

  // Calcular se há seleção válida
  const hasValidCurrentCrop = (() => {
    if (!completedCrop) return false;
    const imgWidth = imgRef.current?.width || displayWidth;
    const imgHeight = imgRef.current?.height || displayHeight;
    const maxX = completedCrop.x + completedCrop.width;
    const maxY = completedCrop.y + completedCrop.height;
    const isPct = completedCrop.unit === '%' && maxX <= 100 && maxY <= 100;

    if (isPct) {
      return completedCrop.width >= 2 && completedCrop.height >= 2;
    } else {
      return completedCrop.width > 20 && completedCrop.height > 20;
    }
  })();

  const ocrMarkersCount = savedMarkers.filter(m => m.type === 'ocr').length + (hasValidCurrentCrop && markerType === 'ocr' ? 1 : 0);
  const highlightMarkersCount = savedMarkers.filter(m => m.type === 'highlight').length + (hasValidCurrentCrop && markerType === 'highlight' ? 1 : 0);
  const totalMarkers = savedMarkers.length + (hasValidCurrentCrop ? 1 : 0);

  const zoomPercent = Math.round(zoomFactor * 100);

  // Obter estilo visual do marcador
  const getMarkerStyle = (marker, index) => {
    if (marker.type === 'ocr') {
      const colorIdx = marker.order % OCR_COLORS.length;
      return {
        backgroundColor: OCR_COLORS[colorIdx].bg,
        border: `3px dashed ${OCR_COLORS[colorIdx].border}`,
        numberBg: OCR_COLORS[colorIdx].border
      };
    } else {
      const matchingColor = HIGHLIGHT_COLORS.find(c => c.border === marker.color) || HIGHLIGHT_COLORS[0];
      return {
        backgroundColor: matchingColor.bg,
        border: `3px solid ${matchingColor.border}`,
        numberBg: matchingColor.border
      };
    }
  };

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      fullScreen
    >
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ gap: 2, flexWrap: 'wrap', py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 2 }}>
            <CropIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
          </Box>

          {/* Seletor de tipo de marcador - Apenas modo markers */}
          {mode === 'markers' && (
            <ToggleButtonGroup
              value={markerType}
              exclusive
              onChange={(_, value) => value && setMarkerType(value)}
              size="small"
              sx={{ mx: 2 }}
            >
              <ToggleButton value="ocr" sx={{ gap: 0.5, px: 2 }}>
                <OcrIcon fontSize="small" />
                OCR
              </ToggleButton>
              <ToggleButton value="highlight" sx={{ gap: 0.5, px: 2 }}>
                <HighlightIcon fontSize="small" />
                Destaque
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Seletor de cor para highlights */}
          {mode === 'markers' && markerType === 'highlight' && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" color="text.secondary">Cor:</Typography>
              {HIGHLIGHT_COLORS.map((color, idx) => (
                <Tooltip key={idx} title={color.name}>
                  <IconButton
                    size="small"
                    onClick={() => setHighlightColorIndex(idx)}
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: color.bg,
                      border: `2px solid ${highlightColorIndex === idx ? color.border : 'transparent'}`,
                      '&:hover': { bgcolor: color.bg, opacity: 0.8 }
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          )}

          {/* Contadores */}
          {mode === 'markers' && ocrMarkersCount > 0 && (
            <Chip
              icon={<OcrIcon fontSize="small" />}
              label={`${ocrMarkersCount} OCR`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {mode === 'markers' && highlightMarkersCount > 0 && (
            <Chip
              icon={<HighlightIcon fontSize="small" />}
              label={`${highlightMarkersCount} Destaque${highlightMarkersCount > 1 ? 's' : ''}`}
              size="small"
              sx={{
                bgcolor: HIGHLIGHT_COLORS[0].bg,
                borderColor: HIGHLIGHT_COLORS[0].border,
                border: '1px solid'
              }}
            />
          )}

          <Box sx={{ flex: 1 }} />

          {/* Controles de zoom */}
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={() => setZoomFactor(Math.max(0.25, zoomFactor - 0.25))}
              disabled={zoomFactor <= 0.25}
            >
              <ZoomOutIcon />
            </IconButton>
            <Slider
              value={zoomFactor}
              onChange={(_, value) => setZoomFactor(value)}
              min={0.25}
              max={2}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              sx={{ width: 120 }}
            />
            <IconButton
              size="small"
              onClick={() => setZoomFactor(Math.min(2, zoomFactor + 0.25))}
              disabled={zoomFactor >= 2}
            >
              <ZoomInIcon />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 50 }}>{zoomPercent}%</Typography>
          </Stack>

          <Box sx={{ width: 16 }} />

          {/* Botões de ação */}
          {mode === 'markers' && (
            <Tooltip title={`Adicionar marcador de ${markerType === 'ocr' ? 'OCR' : 'destaque'}`}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color={markerType === 'ocr' ? 'primary' : 'warning'}
                  startIcon={<AddIcon />}
                  onClick={handleAddMarker}
                  disabled={!hasValidCurrentCrop}
                >
                  Adicionar {markerType === 'ocr' ? 'OCR' : 'Destaque'}
                </Button>
              </span>
            </Tooltip>
          )}

          <Button
            size="small"
            variant="outlined"
            startIcon={<ResetIcon />}
            onClick={handleClearAll}
            disabled={savedMarkers.length === 0 && !completedCrop}
          >
            Limpar
          </Button>

          <IconButton onClick={onClose} color="inherit">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Instruções e marcadores salvos */}
      <Box sx={{ px: 2, py: 1, bgcolor: 'background.paper' }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {mode === 'markers'
              ? <span>Arraste para selecionar áreas. <strong>OCR</strong> para extrair texto, <strong>Destaque</strong> para marcar citações.</span>
              : <span>Arraste o retângulo para selecionar a área da <strong>Thumbnail</strong>.</span>
            }
          </Typography>

          {savedMarkers.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
              {savedMarkers.map((marker, index) => {
                const style = getMarkerStyle(marker, index);
                return (
                  <Chip
                    key={marker.id}
                    icon={marker.type === 'ocr' ? <OcrIcon fontSize="small" /> : <HighlightIcon fontSize="small" />}
                    label={marker.type === 'ocr' ? `OCR ${marker.order + 1}` : `Destaque`}
                    size="small"
                    onDelete={() => handleRemoveMarker(marker.id)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                    sx={{
                      backgroundColor: style.backgroundColor,
                      borderColor: style.numberBg,
                      borderWidth: 2,
                      borderStyle: 'solid',
                      fontWeight: 'bold',
                      '& .MuiChip-deleteIcon': {
                        color: style.numberBg
                      }
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>
      {/* Área principal */}
      <DialogContent
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'grey.900',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {imageSrc && (
          <Box sx={{ 
            position: 'relative', 
            width: 'fit-content', 
            p: 2,
            margin: 'auto'
          }}>
            <ReactCrop
              crop={crop}
              onChange={(c, pc) => setCrop(pc)}
              onComplete={(c, pc) => setCompletedCrop(pc)}
              ruleOfThirds={mode === 'crop'}
              minWidth={5}
              minHeight={5}
            >
              <img
                ref={imgRef}
                src={canvasSafeImageSrc}
                alt="Marcar área"
                crossOrigin="anonymous"
                onLoad={onImageLoad}
                style={{
                  display: 'block',
                  maxWidth: imageLoaded ? 'none' : '100%',
                  maxHeight: imageLoaded ? 'none' : '75vh',
                  width: (imageLoaded && displayWidth) ? `${displayWidth}px` : 'auto',
                  height: (imageLoaded && displayHeight) ? `${displayHeight}px` : 'auto'
                }}
              />
            </ReactCrop>

            {/* Overlays dos marcadores salvos - Apenas no modo markers */}
            {imageLoaded && mode === 'markers' && savedMarkers.map((marker, index) => {
              const style = getMarkerStyle(marker, index);
              return (
                <Box
                  key={marker.id}
                  sx={{
                    position: 'absolute',
                    left: `${marker.percent.x}%`,
                    top: `${marker.percent.y}%`,
                    width: `${marker.percent.width}%`,
                    height: `${marker.percent.height}%`,
                    backgroundColor: style.backgroundColor,
                    border: style.border,
                    boxSizing: 'border-box',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: marker.type === 'ocr' ? 'center' : 'flex-start',
                    justifyContent: marker.type === 'ocr' ? 'center' : 'flex-end',
                    zIndex: 10
                  }}
                >
                  {marker.type === 'ocr' ? (
                    <Box
                      sx={{
                        backgroundColor: style.numberBg,
                        color: 'white',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        boxShadow: 2
                      }}
                    >
                      {marker.order + 1}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        backgroundColor: style.numberBg,
                        color: 'white',
                        borderRadius: 1,
                        px: 0.5,
                        m: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.6rem',
                        boxShadow: 1
                      }}
                    >
                      <HighlightIcon sx={{ fontSize: 10 }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* Footer com ações */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', justifyContent: 'space-between' }}>
        <Box>
          {hasValidCurrentCrop && completedCrop && mode === 'crop' && (
            <Typography variant="body2" color="text.secondary">
              Thumbnail: {(() => {
                const maxX = completedCrop.x + completedCrop.width;
                const maxY = completedCrop.y + completedCrop.height;
                const isPct = completedCrop.unit === '%' && maxX <= 100 && maxY <= 100;
                return isPct
                  ? `${Math.round(completedCrop.width)}% x ${Math.round(completedCrop.height)}%`
                  : `${Math.round(completedCrop.width)}x${Math.round(completedCrop.height)}px`;
              })()}
            </Typography>
          )}
          {hasValidCurrentCrop && completedCrop && mode !== 'crop' && (
            <Typography variant="body2" color="text.secondary">
              Nova área ({markerType === 'ocr' ? 'OCR' : 'Destaque'}): {(() => {
                const maxX = completedCrop.x + completedCrop.width;
                const maxY = completedCrop.y + completedCrop.height;
                const isPct = completedCrop.unit === '%' && maxX <= 100 && maxY <= 100;
                return isPct
                  ? `${Math.round(completedCrop.width)}% x ${Math.round(completedCrop.height)}%`
                  : `${Math.round(completedCrop.width)}x${Math.round(completedCrop.height)}px`;
              })()}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={2}>
          <Button onClick={onClose} color="inherit" size="large">
            Cancelar
          </Button>
          {mode === 'markers' && (
            <Button onClick={handleUseFullImage} color="inherit" size="large">
              Sem Marcações
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            size="large"
            startIcon={<CheckIcon />}
            disabled={mode === 'markers' ? totalMarkers === 0 : !hasValidCurrentCrop}
          >
            {mode === 'markers' ? (
              ocrMarkersCount > 0
                ? `Confirmar (${ocrMarkersCount} OCR${highlightMarkersCount > 0 ? `, ${highlightMarkersCount} destaque${highlightMarkersCount > 1 ? 's' : ''}` : ''})`
                : highlightMarkersCount > 0
                  ? `Confirmar (${highlightMarkersCount} destaque${highlightMarkersCount > 1 ? 's' : ''})`
                  : 'Confirmar Marcações'
            ) : 'Confirmar Recorte'}
          </Button>
        </Stack>
      </DialogActions>
    </PlatformDialog>
  );
};

export default ImageCropDialog;
